import { useEffect, useRef, useState } from 'preact/hooks';
import { useMe, signInPath } from '../auth/useMe';
import { useTranslation } from '../i18n';
import { useToast } from '../primitives/admin';
import {
    type CollectionResponse,
    addWorkToCollection,
    createCollection,
    listCollectionsContainingWork,
    listMyCollections,
    removeWorkFromCollection,
} from '../api/collections';

/**
 * "Add to collection" affordance for the package detail hero. Mirrors
 * the placement and pill shape of the LikeButton: visible only to
 * signed-in callers, opens a click-outside-dismiss popover with the
 * user's collections + checkbox toggles + an inline "new collection"
 * affordance.
 *
 * <p>Membership state is loaded lazily the first time the popover
 * opens (one indexed query against UserCollectionMember). Toggles use
 * optimistic local state, rolling back on network failure. The "+ new"
 * row prompts for a name, normalises a slug client-side, and POSTs to
 * /api/me/collections; on success the package is auto-added.</p>
 */
export function CollectionsButton({
    workId,
    initialInAny,
}: {
    workId: string;
    initialInAny: boolean;
}) {
    const me = useMe();
    const t = useTranslation();
    const toast = useToast();
    const signedIn = me.status === 'authenticated';

    const [open, setOpen] = useState(false);
    const [collections, setCollections] = useState<CollectionResponse[] | null>(null);
    const [membership, setMembership] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [busySlug, setBusySlug] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [inAny, setInAny] = useState(initialInAny);

    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const ensureLoaded = async () => {
        if (collections !== null) return;
        setLoading(true);
        try {
            const [cols, contains] = await Promise.all([
                listMyCollections(),
                listCollectionsContainingWork(workId),
            ]);
            setCollections(cols);
            setMembership(new Set(contains));
            setInAny(contains.length > 0);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', `Couldn't load collections: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    const onOpen = () => {
        setOpen(true);
        void ensureLoaded();
    };

    const toggle = async (col: CollectionResponse) => {
        if (busySlug) return;
        const wasIn = membership.has(col.slug);
        setBusySlug(col.slug);
        const next = new Set(membership);
        if (wasIn) next.delete(col.slug);
        else next.add(col.slug);
        setMembership(next);
        setInAny(next.size > 0);
        try {
            if (wasIn) await removeWorkFromCollection(col.slug, workId);
            else await addWorkToCollection(col.slug, workId);
        } catch (err) {
            const revert = new Set(membership);
            setMembership(revert);
            setInAny(revert.size > 0);
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', `Update failed: ${msg}`);
        } finally {
            setBusySlug(null);
        }
    };

    const onCreate = async () => {
        const name = newName.trim();
        if (!name || creating) return;
        const slug = slugify(name);
        if (!slug) {
            toast.push('error', 'Name needs at least one letter or number.');
            return;
        }
        setCreating(true);
        try {
            const col = await createCollection({ slug, name });
            await addWorkToCollection(col.slug, workId);
            const cols = collections ?? [];
            setCollections([col, ...cols]);
            const next = new Set(membership);
            next.add(col.slug);
            setMembership(next);
            setInAny(true);
            setNewName('');
            toast.push('success', `Added to "${col.name}".`);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', `Couldn't create collection: ${msg}`);
        } finally {
            setCreating(false);
        }
    };

    // While the /me probe is in flight, render nothing rather than flashing a
    // misleading "sign in" ghost at an already-authenticated user.
    if (me.status === 'loading') return null;
    // Anonymous (or probe error): render a ghost "collect" affordance that
    // nudges to sign-in and returns here afterward, instead of rendering
    // nothing at the point of intent. The nudge string is the accessible name
    // (not just a tooltip) so assistive tech announces where the link goes.
    if (!signedIn) {
        return (
            <a
                href={signInPath()}
                title={t('work.collections.signInNudge')}
                aria-label={t('work.collections.signInNudge')}
                class="inline-flex items-center gap-2 rounded-full border border-dashed border-rule px-4 py-2.5 text-sm text-fg-faint outline-none transition-colors hover:border-fg-muted hover:text-fg-secondary focus-visible:ring-2 focus-visible:ring-accent"
            >
                <CollectionIcon filled={false} />
                {t('work.collections.addLabel')}
            </a>
        );
    }

    return (
        <div ref={rootRef} class="relative inline-block">
            <button
                type="button"
                onClick={() => (open ? setOpen(false) : onOpen())}
                aria-haspopup="dialog"
                aria-expanded={open}
                class={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-colors ${
                    inAny
                        ? 'border-accent bg-accent-glow text-accent hover:bg-accent/10'
                        : 'border-rule-strong text-fg-muted hover:border-fg-muted hover:text-fg-primary'
                }`}
            >
                <CollectionIcon filled={inAny} />
                {inAny
                    ? t('work.collections.inLabel')
                    : t('work.collections.addLabel')}
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-label={t('work.collections.dialogLabel')}
                    class="absolute right-0 z-40 mt-2 w-[320px] origin-top-right rounded-md border border-rule-strong bg-surface-raised shadow-xl"
                >
                    <div class="border-b border-rule px-3.5 py-2.5">
                        <div class="font-mono text-[10.5px] uppercase tracking-[0.14em] text-fg-faint">
                            {t('work.collections.eyebrow')}
                        </div>
                    </div>
                    <div class="max-h-[260px] overflow-auto py-1">
                        {loading && collections === null && (
                            <div class="px-3.5 py-3 text-sm text-fg-muted">
                                {t('common.loading.default')}
                            </div>
                        )}
                        {collections !== null && collections.length === 0 && (
                            <div class="px-3.5 py-3 text-sm text-fg-muted">
                                {t('work.collections.empty')}
                            </div>
                        )}
                        {collections?.map((col) => {
                            const isIn = membership.has(col.slug);
                            const busy = busySlug === col.slug;
                            return (
                                <button
                                    key={col.id}
                                    type="button"
                                    onClick={() => void toggle(col)}
                                    disabled={busy}
                                    class={`flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm transition-colors hover:bg-surface-base ${
                                        busy ? 'opacity-60' : ''
                                    }`}
                                >
                                    <span
                                        aria-hidden="true"
                                        class={`grid h-4 w-4 place-items-center rounded border ${
                                            isIn
                                                ? 'border-accent bg-accent text-surface-base'
                                                : 'border-rule-strong text-transparent'
                                        }`}
                                    >
                                        <CheckGlyph />
                                    </span>
                                    <span class="flex-1 truncate text-fg-primary">{col.name}</span>
                                    <span class="font-mono text-[10.5px] uppercase tracking-[0.12em] text-fg-faint">
                                        {col.memberCount}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div class="border-t border-rule px-3.5 py-2.5">
                        <div class="flex items-center gap-2">
                            <input
                                type="text"
                                value={newName}
                                onInput={(e) =>
                                    setNewName((e.target as HTMLInputElement).value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        void onCreate();
                                    }
                                }}
                                placeholder={t('work.collections.newPlaceholder')}
                                class="flex-1 rounded border border-rule bg-surface-base px-2.5 py-1.5 text-sm text-fg-primary placeholder:text-fg-faint outline-none focus:border-accent"
                            />
                            <button
                                type="button"
                                onClick={() => void onCreate()}
                                disabled={creating || !newName.trim()}
                                class="rounded border border-accent bg-accent px-3 py-1.5 text-sm text-surface-base transition-colors hover:bg-fg-primary hover:text-surface-base disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {creating ? '…' : t('work.collections.newSubmit')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CollectionIcon({ filled }: { filled: boolean }) {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={filled ? 'currentColor' : 'none'}
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
        >
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
    );
}

function CheckGlyph() {
    return (
        <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

/**
 * Mirror of CollectionsController.SlugNormalise for client-side
 * validation: lowercase, hyphen-separated alphanumeric run. Server
 * re-normalises on POST so this is purely for instant feedback.
 */
function slugify(input: string): string {
    const lower = input.trim().toLowerCase();
    const out: string[] = [];
    let prevDash = true;
    for (const ch of lower) {
        if (/[a-z0-9]/.test(ch)) {
            out.push(ch);
            prevDash = false;
        } else if (!prevDash) {
            out.push('-');
            prevDash = true;
        }
    }
    return out.join('').replace(/-+$/, '');
}
